import { Type, Expose, Transform } from 'class-transformer';
import { WccIntegrationStatusType } from '@shared/enums/wcc-integration-status-type.enum';
import { ShipmentCreationTemplateTypes } from '@shared/enums/shipment-creation-template-types.enum';
import { CustomerType } from '@shared/enums/customer-type.enum';
import { CustomerPriceType } from '@shared-features/enums/customer-price-type';
import { ShipmentStatus } from '@shared-features/enums/shipment-status.enum';
import { ContactPerson } from '../../customers/models/contact-person';
import { Customer } from '../../customers/models/customer';
import { Driver } from '../../fleets/models/driver';
import { Fleet } from '../../fleets/models/fleet';
import { LocationModel } from '../../live-tracking/models/location.model';
import { Model } from './model';
import { ProductType } from '../../shipments/models/product-type';
import { Receiver } from '../../receivers/models/receiver';
import { Transaction } from '../../fleets/models/transaction';
import { TruckType } from '../../fleets/models/truck-type';
import { User } from '../../users/models/user';
import { ShipmentDeliveryNote } from '../../shipments/models/shipment-delivery-note';
import { ShipmentTransportationPayment } from '../../shipments/models/shipment-transportation-payment';
import { MeasurementUnit } from '../../shipments/models/measurement-unit.model';
import { ShipmentRequest } from '../../shipments/models/shipment-request.model';
import { Trailer } from '../../fleets/models/trailer.model';
import { Truck } from '../../fleets/models/truck';
import { ShipmentLabel } from '../../shipments/models/shipment-label.model';
import { ShipmentInvoice } from '../../shipments/models/shipment-invoice.model';
import { RequestLog } from './request-log.model';
import { InvoiceStatus } from '@features/invoices/enums/invoice-status.enum';
import { ShipmentHistoryItem } from '@features/shipments/models/shipment-history-item.model';
import { GateDefinition } from '@shared/model/gate-definition.model';
import { Media } from './media';
import { ShipmentMetadata } from '@shared-features/models/shipment-metadata.model';
import { InvoicesStatus } from '@features/invoices/enums/invoices-status.enum';
import { BayanWayBill } from '@shared-features/models/bayan-way-bill.model';
import { DynamicField } from '@shared-features/models/dynamic-field.model';
import { UserInvoiceType } from '@shared-features/enums/user-invoice-type.enum';
import { VatType } from '@shared-features/enums/vat-type.enum';
import { ShipmentCreationType } from '../../shipments/enums/shipment-creation-type.enum';

export class Shipment extends Model {
  @Expose()
  id: number = null;

  @Expose()
  uuid: string;

  @Expose()
  code = '';

  @Expose()
  name = '';

  @Expose()
  status: ShipmentStatus;

  @Expose()
  weight: number;

  @Expose()
  fleetSpotRate: number;

  @Expose()
  customerSpotRate: number;

  @Expose()
  isFreeLanced: boolean;

  @Expose()
  notes: string;

  @Type(() => Customer)
  @Expose()
  customer: Customer = new Customer();

  @Type(() => Customer)
  @Expose()
  childCustomer: Customer = new Customer();

  @Type(() => ProductType)
  @Expose()
  productType: ProductType = new ProductType();

  @Type(() => TruckType)
  @Expose()
  truckType: TruckType = new TruckType();

  @Type(() => TruckType)
  @Expose()
  optionalTruckTypes: TruckType[];

  @Type(() => Receiver)
  @Expose()
  receiver: Receiver = new Receiver();

  @Type(() => Driver)
  @Expose()
  driver: Driver = new Driver();

  @Type(() => Truck)
  @Expose()
  truck: Truck = new Truck();

  @Type(() => Fleet)
  @Expose()
  fleet: Fleet = new Fleet();

  @Type(() => LocationModel)
  @Expose()
  pickUpLocation: LocationModel = new LocationModel();

  @Type(() => LocationModel)
  @Expose()
  dropOffLocation: LocationModel = new LocationModel();

  @Expose()
  @Type(() => ShipmentHistoryItem)
  history: ShipmentHistoryItem[] = [];

  @Type(() => Transaction)
  @Expose()
  transactions: Transaction[] = [];

  @Expose()
  repeatedNumber: number = null;

  @Type(() => LocationModel)
  @Expose()
  receiverLocation: LocationModel = new LocationModel();

  @Type(() => ContactPerson)
  @Expose()
  contactPerson: ContactPerson = new ContactPerson();

  @Expose()
  @Transform(({ value }) => (value ? new Date(value) : null), { toClassOnly: true })
  pickedDateTime: Date;

  @Expose()
  @Transform(({ value }) => (value ? new Date(value) : null), { toClassOnly: true })
  deliveryDate: Date;

  @Expose()
  @Transform(({ value }) => (value ? new Date(value) : null), { toClassOnly: true })
  pickUpDate: Date;

  @Expose()
  @Transform(({ value }) => (value ? new Date(value) : null), { toClassOnly: true })
  createdAt: Date;

  @Expose()
  @Transform(({ value }) => (value ? new Date(value) : null), { toClassOnly: true })
  updatedAt: number;

  @Expose()
  totalFleetCost: number;

  @Expose()
  creatorType: string;

  @Type(() => User)
  @Expose()
  createdBy: User = new User();

  @Type(() => User)
  @Expose()
  updatedBy?: User = new User();

  @Expose()
  priceComment: string;

  @Expose()
  customerPriceType: CustomerPriceType;

  @Expose()
  detentionCharge: number;

  @Expose()
  returnCharge: number;

  @Expose()
  multiDestinationCharge: number;

  @Expose()
  deliveryNoteNumber: string;

  @Expose()
  deliveryNoteComment: string;

  @Expose()
  shortDynamicLink: string;

  @Type(() => ShipmentDeliveryNote)
  @Expose()
  deliveryNote: ShipmentDeliveryNote[] = [];

  @Expose()
  archivedTrackingUrl: string;

  @Expose()
  archivedAvlTrackingUrl: string;

  @Expose()
  otp: string;

  @Type(() => ShipmentTransportationPayment)
  @Expose()
  transportationPayment: ShipmentTransportationPayment = new ShipmentTransportationPayment();

  @Expose()
  etaTime: number;

  @Expose()
  isPaidForDriver: boolean;

  @Expose()
  weightMeasurementUnit: MeasurementUnit;

  @Expose()
  quantity: number;

  @Expose()
  delayedAction: boolean;

  @Expose()
  shipmentDelayed: boolean;

  @Expose()
  driverAllowance: number;

  selected?: boolean = false;

  @Type(() => ShipmentRequest)
  @Expose()
  requests: ShipmentRequest[];

  @Type(() => Trailer)
  @Expose()
  trailer: Trailer;

  @Expose() droppingOffTime: number;
  @Expose() loadingOffTime: number;

  @Expose()
  isMobileTrackingAvailable: boolean;

  @Expose()
  isAvlTrackingAvailable: boolean;

  @Expose()
  customerType: CustomerType;

  @Type(() => ShipmentLabel)
  @Expose()
  label: ShipmentLabel[] = [];

  @Expose() noOfRequests: number;

  @Type(() => ShipmentInvoice)
  @Expose()
  invoice: ShipmentInvoice = new ShipmentInvoice();

  @Type(() => ShipmentInvoice)
  @Expose()
  saasInvoice: ShipmentInvoice = new ShipmentInvoice();

  @Type(() => ShipmentInvoice)
  @Expose()
  fleetInvoice: ShipmentInvoice = new ShipmentInvoice();

  @Expose()
  totalPrice: number;

  @Expose()
  isVerifiedByBayan: boolean;

  @Expose()
  isBayanReady: boolean;

  @Type(() => RequestLog)
  @Expose()
  requestLogHistory: RequestLog[] = [];

  @Expose()
  shipmentCreationTemplate: ShipmentCreationTemplateTypes;

  @Type(() => Customer)
  @Expose()
  dropOffCustomer: Customer = new Customer();

  @Type(() => User)
  @Expose()
  dropOffUser: User = new User();

  @Expose()
  loadedAmount: number;

  @Expose()
  @Transform(({ value }) => (value ? new Date(value) : null), { toClassOnly: true })
  lastJobUpdate: Date;

  @Expose()
  wccId: number;

  @Expose()
  productPrice: number;

  @Expose()
  totalProductPrice: number;

  @Expose()
  wccIntegrationStatus: WccIntegrationStatusType;

  @Expose()
  customerPrice: number;

  @Expose()
  fleetPrice: number;

  @Expose()
  fleetInvoiceStatus?: InvoicesStatus;

  @Expose()
  customerInvoiceStatus?: InvoiceStatus;

  @Expose()
  gate?: GateDefinition;

  @Expose()
  numOfPallets?: number;

  @Expose()
  noOfPallets?: number;

  @Expose()
  noOfCustomers?: number;

  @Expose()
  noOfOrders: number;

  @Expose()
  etaInSeconds: Date;

  @Expose()
  waitingChargeProfAttachment: Media;

  @Expose()
  waitingChargeProfId: number;

  @Expose()
  @Type(() => ShipmentMetadata)
  shipmentMetadata?: ShipmentMetadata;

  @Expose()
  violated: boolean;

  @Expose()
  isPlanned: boolean;

  @Expose()
  deliveryNoteReference: string;

  @Expose() startShipment: boolean;
  @Expose() forceClose: boolean;
  @Expose() assignAndAutoStart: boolean;
  @Expose() comment: string;

  @Expose() customerName: string;
  @Type(() => BayanWayBill)
  @Expose()
  bayanWaybills?: BayanWayBill[] = [];

  @Type(() => DynamicField)
  @Expose()
  dynamicFields: DynamicField[];

  @Expose() hasDynamicFields?: boolean;
  @Expose() serviceType?: UserInvoiceType;

  @Expose()
  isBroadcasted: boolean;

  @Expose()
  saasInvoiceStatus: InvoiceStatus;

  @Expose()
  shipmentCreationType: ShipmentCreationType;

  @Expose()
  vatType: VatType;

  @Expose()
  volume: number;

  @Expose()
  referenceNumber: number;

  @Expose()
  fleetReferenceNumber: number;

  @Expose()
  bayanTripId: number;

  @Expose()
  bayanWaybillId: number;

  @Expose()
  pickupGateName: string;

  @Expose()
  autoDispatching: boolean;

  constructor() {
    super();
  }

  public get shipmentName() {
    if (this.name) {
      return '#' + this.name;
    } else {
      return '#' + this.code;
    }
  }

  /**
   * The shipments-list endpoint returns these as flat columns on `ShipmentList`, while the
   * details endpoints only carry the nested source data. They are exposed here under the same
   * names so anything rendering a shipment can read them off a single model.
   *
   * `cost`/`price` are totals, not the base line items: `transactions[0].fleetPrice` and
   * `customerPrice` are only the base charges and are `null` on shipments billed purely on
   * extras. Verified against the list endpoint over 240 staging shipments - 0 mismatches.
   * Not to be confused with the `totalFleetCost`/`totalPrice` fields on this class, which the
   * details endpoints leave unpopulated (`totalPrice` comes back `null`).
   */
  get from(): string {
    return this.pickUpLocation?.city?.name;
  }

  get to(): string {
    return this.dropOffLocation?.city?.name;
  }

  get cost(): number {
    return Number(this.transactions?.[0]?.totalFleetCost || 0);
  }

  /**
   * Base customer charge plus the customer-side extras listed below. Two caveats, both tracked
   * on PR 31382:
   * - the hourly waiting term is included here but the e-waybill prints no hourly row, so on an
   *   hourly-billed shipment the printed lines do not visibly add up to this total;
   * - `customerReturnCharge`, `damageCost` and `advancedPayment` are NOT included. Whether the
   *   list's `price` column includes them is unverified - the 240-shipment check that validated
   *   this sum did not record whether any sample had them set.
   * There is no customer-side equivalent of `totalFleetCost` on the `/financial` payload (its
   * only totals are `totalFleetCost` and `CODAmount`), so this cannot be made backend-authoritative
   * without a new field.
   */
  get price(): number {
    const transaction = this.transactions?.[0];
    const amount = (value: number | string) => Number(value || 0);

    return (
      amount(transaction?.customerPrice) +
      amount(transaction?.multiDestinationChargePrice) +
      amount(transaction?.customerWaitingChargePerDay) * amount(transaction?.waitingDays) +
      amount(transaction?.customerWaitingChargePerHour) * amount(transaction?.waitingHours) +
      amount(transaction?.weightingFine) +
      amount(transaction?.customerClearanceFees)
    );
  }

  get margin(): number {
    return this.price - this.cost;
  }
}
